/**
 * results.ts — the results screen shown when an episode finishes.
 *
 * A centered overlay card with the headline metric (final catch success rate,
 * framed as the "how well did the agent learn" number), plus supporting
 * stats and a one-line verdict. The viewer can press Play again to return to
 * the start dialogue.
 */

export interface ResultsStats {
  /** Total rounds played. */
  rounds: number;
  /** Total casts (= rounds). */
  casts: number;
  /** Total catches. */
  catches: number;
  /** Total yield in kg. */
  weightKg: number;
  /** Cumulative regret at the end. */
  cumulativeRegret: number;
  /** Round number of the first catch, or null if none. */
  firstCatchRound: number | null;
  /** Catch outcomes for the final 20 rounds (true = caught), for the
   *  headline success-rate metric. */
  finalWindow: boolean[];
}

export class ResultsScreen {
  readonly el: HTMLElement;

  constructor(stats: ResultsStats, onReplay: () => void) {
    // Headline: success rate over the final window. This is the most
    // intuitive "did it learn" number — a random agent is ~30%, a learned
    // agent reaches 60–80%.
    console.log(stats.finalWindow)
    const finalN = stats.finalWindow.length;
    const finalCatches = stats.finalWindow.filter(Boolean).length;
    const finalRate = finalN > 0 ? (finalCatches / finalN) * 100 : 0;
    const overallRate = stats.casts > 0 ? (stats.catches / stats.casts) * 100 : 0;

    const verdict = makeVerdict(finalRate);
    const firstCatch =
      stats.firstCatchRound === null
        ? 'never'
        : `round ${stats.firstCatchRound}`;

    this.el = document.createElement('div');
    this.el.className = 'overlay';
    this.el.innerHTML = `
      <div class="overlay-card results">
        <h2>Episode complete</h2>
        <div class="result-headline">
          <div class="result-big">${finalRate.toFixed(0)}%</div>
          <div class="result-big-label">catch rate, last ${finalN} rounds</div>
        </div>
        <p class="result-verdict">${verdict}</p>
        <div class="result-grid">
          <div class="result-cell">
            <div class="result-cell-label">first catch</div>
            <div class="result-cell-val">${firstCatch}</div>
          </div>
          <div class="result-cell">
            <div class="result-cell-label">overall rate</div>
            <div class="result-cell-val">${overallRate.toFixed(0)}%</div>
          </div>
          <div class="result-cell">
            <div class="result-cell-label">yield</div>
            <div class="result-cell-val">${stats.weightKg.toFixed(1)} kg</div>
          </div>
          <div class="result-cell">
            <div class="result-cell-label">regret</div>
            <div class="result-cell-val">${stats.cumulativeRegret.toFixed(2)}</div>
          </div>
        </div>
        <button class="play-btn">↻ Play again</button>
      </div>
    `;
    this.el.querySelector<HTMLButtonElement>('.play-btn')!.addEventListener('click', onReplay);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.el);
  }

  remove(): void {
    this.el.remove();
  }
}

/**
 * Produce a one-line textual verdict from the final-window success rate.
 * The thresholds are calibrated to the reward landscape: a random agent
 * scores ~30%, a converged agent 60%+.
 */
function makeVerdict(finalRate: number): string {
  if (finalRate >= 70) return 'The agent learned to track the flock well.';
  if (finalRate >= 55) return 'The agent found the flock and is catching reliably.';
  if (finalRate >= 40) return "The agent is learning but hasn't fully converged — try more rounds or less drift.";
  if (finalRate >= 25) return 'The agent barely improved on random — try more exploration or a larger window.';
  return 'The agent failed to learn — check the hyperparameters.';
}
